import axios, { AxiosError } from 'axios';

/**
 * Extracts a user-friendly error message from an API error
 * @param error - The error object from an API call
 * @param fallbackMessage - Optional fallback message if no specific error can be extracted
 * @returns A user-friendly error message
 */
export const getErrorMessage = (
  error: unknown, 
  fallbackMessage: string = 'An unexpected error occurred. Please try again later.'
): string => {
  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ error?: string; message?: string }>;
    
    // Check for error message in response data
    if (axiosError.response?.data) {
      const { error: errorMsg, message } = axiosError.response.data;
      if (errorMsg) return errorMsg;
      if (message) return message;
    }
    
    // Check for specific HTTP status codes
    if (axiosError.response?.status) {
      switch (axiosError.response.status) {
        case 400:
          return 'Invalid request. Please check your input and try again.';
        case 401:
          return 'You are not authorized to perform this action. Please log in again.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested resource was not found.';
        case 409:
          return 'This operation cannot be completed due to a conflict with the current state of the resource.';
        case 422:
          return 'The request was well-formed but contains semantic errors.';
        case 429:
          return 'Too many requests. Please try again later.';
        case 500:
          return 'An internal server error occurred. Please try again later.';
        default:
          return `Server error (${axiosError.response.status}). Please try again later.`;
      }
    }
    
    // Network errors
    if (axiosError.code === 'ECONNABORTED') {
      return 'The request timed out. Please check your internet connection and try again.';
    }
    
    if (axiosError.message) {
      return axiosError.message;
    }
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Default fallback
  return fallbackMessage;
};

/**
 * Logs an error to the console with additional context
 * @param error - The error object
 * @param context - Additional context about where the error occurred
 */
export const logError = (error: unknown, context: string): void => {
  console.error(`Error in ${context}:`, error);
  
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    console.error('API Error Details:', {
      status: axiosError.response?.status,
      data: axiosError.response?.data,
      headers: axiosError.response?.headers,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        baseURL: axiosError.config?.baseURL,
      }
    });
  }
};
